import {
  DropDownButton,
  type DropDownButtonProps,
} from '@progress/kendo-react-buttons'
import { useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { Icon } from '~/components/elements/Icon'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery'
import {
  applyIfExist,
  compact,
  ensureExists,
  exists,
  findByPath,
  type SurveyFilterItem,
} from '~/utils'

import { dialogInfoAtom } from '../../atoms'
import { useAvailableCustomerAttributeGroups } from '../../hooks/useAvailableCustomerAttributeGroups'

import styles from './CreateInBulkSelectFiltersDialog.css'
import { FilterInput } from './FilterInput'
import { type InputValues, resolver } from './utils'

type Filter = InputValues['filters'][number]

type AddButtonItem = {
  label: string
  value: Filter['category']
}

export const CreateInBulkSelectFiltersDialog = () => {
  const { t } = useTranslation()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const questions = useSurveyQueryQuestions()
  const customerAttributeGroups = useAvailableCustomerAttributeGroups(undefined)

  const firstQuestionFilter = React.useMemo<Filter | undefined>(
    () =>
      R.pipe(
        questions,
        R.first(),
        applyIfExist((question) => ({
          category: 'Question',
          id: question.question_id,
        })),
      ),
    [questions],
  )

  const firstCustomerAttributeGroupFilter = React.useMemo<Filter | undefined>(
    () =>
      R.pipe(
        customerAttributeGroups,
        R.first(),
        applyIfExist((customerAttributeGroup) => ({
          category: 'CustomerAttribute',
          id: customerAttributeGroup.id,
        })),
      ),
    [customerAttributeGroups],
  )

  const methods = useForm<InputValues>({ resolver })
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = methods
  const { append, fields, remove } = useFieldArray({ control, name: 'filters' })

  const questionnaireLabel = t('questionnaire')
  const customizedSegmentsGroupLabel = t('customized_segments_group')
  const addButtonItems = React.useMemo<AddButtonItem[]>(
    () =>
      compact([
        exists(firstQuestionFilter) && {
          label: questionnaireLabel,
          value: 'Question',
        },
        exists(firstCustomerAttributeGroupFilter) && {
          label: customizedSegmentsGroupLabel,
          value: 'CustomerAttribute',
        },
      ]),
    [
      customizedSegmentsGroupLabel,
      firstCustomerAttributeGroupFilter,
      firstQuestionFilter,
      questionnaireLabel,
    ],
  )

  const handleAddButtonItemClick = React.useCallback<
    EH<DropDownButtonProps, 'onItemClick'>
  >(
    (event) => {
      const category = event.item.value as Filter['category']
      const filter = match(category)
        .with('Question', () => firstQuestionFilter)
        .with('CustomerAttribute', () => firstCustomerAttributeGroupFilter)
        .exhaustive()
      if (exists(filter)) {
        append(filter)
      }
    },
    [append, firstCustomerAttributeGroupFilter, firstQuestionFilter],
  )

  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ filters }) => {
      setDialogInfo({
        dialogType: 'createInBulkSelectCustomerAttributes',
        filterGroupsArray: R.pipe(
          filters,
          R.map((filter) =>
            match<(typeof filters)[number], SurveyFilterItem[]>(filter)
              .with({ category: 'Question' }, ({ category, id }) =>
                R.pipe(
                  questions,
                  findByPath('question_id', id),
                  ensureExists,
                  (question) =>
                    question.answer_options.map((answerOption) => ({
                      category,
                      question: {
                        questionId: question.question_id,
                        operation: 'equal',
                        answerOptions: [
                          R.pick(answerOption, ['option', 'text']),
                        ],
                      },
                    })),
                ),
              )
              .with({ category: 'CustomerAttribute' }, ({ category, id }) =>
                R.pipe(
                  customerAttributeGroups,
                  findByPath('id', id),
                  ensureExists,
                  (customerAttributeGroup) =>
                    customerAttributeGroup.customerAttributes.map(
                      (customerAttribute) => ({
                        category,
                        customerAttribute: {
                          customerAttributeIds: [customerAttribute.id],
                          operation: 'equal',
                        },
                      }),
                    ),
                ),
              )
              .exhaustive(),
          ),
          (intermediateFilterGroups) =>
            intermediateFilterGroups.reduce<SurveyFilterItem[][]>(
              (prev, intermediateFilterGroup) =>
                R.isEmpty(prev)
                  ? intermediateFilterGroup.map((intermediateFilter) => [
                      intermediateFilter,
                    ])
                  : prev.flatMap((prevIntermediateFilterGroup) =>
                      intermediateFilterGroup.map((intermediateFilter) => [
                        ...prevIntermediateFilterGroup,
                        intermediateFilter,
                      ]),
                    ),
              [],
            ),
          R.map((filterGroup) => [filterGroup]),
        ),
      })
    },
    [customerAttributeGroups, questions, setDialogInfo],
  )

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        disabledExecute={!isValid}
        executeButtonText={t('next')}
        mode="create"
        title={t('add_customized_segments_in_batch')}
        onCancel={resetDialogInfo}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack spacing={4}>
          <Typography variant="normal">
            {t(
              'automatically_generate_all_patterns_by_combining_questionnaire_responses_and_customized_segments',
            )}
          </Typography>

          {0 < fields.length && (
            <VStack spacing={2}>
              {fields.map((field, index) => (
                <React.Fragment key={field.id}>
                  {index !== 0 && (
                    <Icon className={styles.crossIcon} icon="x" size="1rem" />
                  )}

                  <FilterInput index={index} onDelete={remove} />
                </React.Fragment>
              ))}
            </VStack>
          )}

          <DropDownButton
            className={styles.addButton}
            icon="plus-outline"
            items={addButtonItems}
            text={t('add')}
            textField="label"
            onItemClick={handleAddButtonItemClick}
          />
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
